import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {Typography, Avatar, Menu, MenuItem, Grid, Card, CardMedia, Box, Container, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, FormControl, InputLabel, Select, Chip} from '@mui/material'
import styles from './Panel.module.css';

import Logo from '../../assets/logo_white.png';
import UserPhoto from './../../assets/Panel/userPhoto.png';
import customIcon1 from '../../assets/PigeonsCatalog/pearson_catalog.svg';
import customIcon2 from '../../assets/PigeonsCatalog/wing_catalog.svg';
import customIcon3 from '../../assets/PigeonsCatalog/pair_catalog.svg';
import customIcon4 from '../../assets/PigeonsCatalog/genealogy_catalog.svg';
import customIcon5 from '../../assets/PigeonsCatalog/flight_catalog.svg';


import AvatarImage from '../../assets/PigeonsCatalog/Avatar.png';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import EditIcon from '@mui/icons-material/Edit';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { orange } from '@mui/material/colors';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { Bird, Trophy, Medal, Star, Wind } from 'lucide-react';

const Panel = () => {
    const navigate = useNavigate();
    const { planId, planLabel, updatePlan } = useSubscription();
    
    // Funkcja do odczytu liczby gołębi z localStorage
    const getPigeonsCount = () => {
        const count = localStorage.getItem('pigeonsCount');
        return count ? parseInt(count, 10) : 0;
    };

    const [pigeonsCount, setPigeonsCount] = useState(getPigeonsCount());
    const [flightsStats, setFlightsStats] = useState({
        totalFlights: 0,
        top1Count: 0,
        top2Count: 0,
        top50Count: 0
    });

    // Funkcja do obliczania statystyk lotów
    const calculateFlightsStats = () => {
        const savedFlights = localStorage.getItem('flightsData');
        if (savedFlights) {
            try {
                const flights = JSON.parse(savedFlights);
                // Filtruj tylko ukończone loty
                const completedFlights = flights.filter(f => f.status === 'completed');
                
                const totalFlights = completedFlights.length;
                const top1Count = completedFlights.filter(f => f.position === 1).length;
                const top2Count = completedFlights.filter(f => f.position === 2).length;
                const top50Count = completedFlights.filter(f => 
                    (f.rankingPosition && f.rankingPosition <= 50) || 
                    (f.position && f.position <= 50)
                ).length;
                
                return {
                    totalFlights,
                    top1Count,
                    top2Count,
                    top50Count
                };
            } catch (error) {
                console.error('Błąd wczytywania statystyk lotów:', error);
            }
        }
        return {
            totalFlights: 0,
            top1Count: 0,
            top2Count: 0,
            top50Count: 0
        };
    };

    // Aktualizuj statystyki lotów
    React.useEffect(() => {
        const updateStats = () => {
            const stats = calculateFlightsStats();
            setFlightsStats(stats);
        };

        // Nasłuchuj zmian w localStorage dla lotów
        const handleStorageChange = (e) => {
            if (e.key === 'flightsData') {
                updateStats();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        
        // Sprawdzaj zmiany co 500ms (dla tego samego okna przeglądarki)
        const interval = setInterval(updateStats, 500);

        // Aktualizuj przy montowaniu komponentu
        updateStats();

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(interval);
        };
    }, []);

    // Funkcje do zarządzania danymi hodowli
    const getBreederData = () => {
        const saved = localStorage.getItem('breederData');
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (error) {
                console.error('Błąd wczytywania danych hodowli:', error);
            }
        }
        return {
            name: '',
            logo: null,
            address: '',
            lat: null,
            lng: null
        };
    };

    const [breederData, setBreederData] = useState(getBreederData());
    const [editBreederOpen, setEditBreederOpen] = useState(false);
    const [breederForm, setBreederForm] = useState({
        name: breederData.name || '',
        logo: breederData.logo || null,
        address: breederData.address || '',
        lat: breederData.lat || null,
        lng: breederData.lng || null
    });
    const [breederMapOpen, setBreederMapOpen] = useState(false);
    const [breederAddressSearch, setBreederAddressSearch] = useState('');
    const [breederMapCenter, setBreederMapCenter] = useState({ lat: 52.2297, lng: 21.0122 });
    const [breederMapLocation, setBreederMapLocation] = useState(
        breederData.lat && breederData.lng ? { lat: breederData.lat, lng: breederData.lng } : null
    );

    // Wczytaj dane hodowli przy starcie
    React.useEffect(() => {
        const data = getBreederData();
        setBreederData(data);
        setBreederForm({
            name: data.name || '',
            logo: data.logo || null,
            address: data.address || '',
            lat: data.lat || null,
            lng: data.lng || null
        });
        if (data.lat && data.lng) {
            setBreederMapCenter({ lat: data.lat, lng: data.lng });
            setBreederMapLocation({ lat: data.lat, lng: data.lng });
        }
    }, []);

    // Aktualizuj liczbę gołębi przy każdym renderze i nasłuchuj zmian
    React.useEffect(() => {
        const updateCount = () => {
            const newCount = getPigeonsCount();
            if (newCount !== pigeonsCount) {
                setPigeonsCount(newCount);
            }
        };

        // Nasłuchuj zmian w localStorage (dla innych okien/tabów)
        const handleStorageChange = (e) => {
            if (e.key === 'pigeonsCount') {
                updateCount();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        
        // Sprawdzaj zmiany co 500ms (dla tego samego okna przeglądarki)
        const interval = setInterval(updateCount, 500);

        // Aktualizuj przy montowaniu komponentu
        updateCount();

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(interval);
        };
    }, [pigeonsCount]);

    const data = {
        pigeonsAmount: pigeonsCount,
        winner: flightsStats.top1Count, // Liczba lotów z pozycją 1
        flightAt: flightsStats.totalFlights, // Liczba ukończonych lotów
        top: flightsStats.top50Count // Liczba lotów w top 50
    };
    
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [editProfileOpen, setEditProfileOpen] = useState(false);
    const [subscriptionSettingsOpen, setSubscriptionSettingsOpen] = useState(false);
    const [billingDetails, setBillingDetails] = useState(() => {
        const saved = localStorage.getItem('billingDetails');
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (error) {
                console.error('Blad wczytywania szczegolow platnosci:', error);
            }
        }
        return {
            companyName: '',
            taxId: '',
            billingEmail: '',
            paymentMethodNote: 'Karta testowa',
        };
    });
    const [profileData, setProfileData] = useState({
        firstName: 'Krzysztof',
        lastName: 'Nowak',
        city: 'Łódź',
        email: 'k.nowak@gmail.com',
        phone: '600 500 400',
        address: '',
        photo: UserPhoto
    });

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        setAnchorEl(null);
        // Wyczyść dane użytkownika z localStorage (opcjonalnie)
        // localStorage.clear(); // Odkomentuj jeśli chcesz wyczyścić wszystkie dane
        navigate('/');
    };

    const handleEditProfile = () => {
        setEditProfileOpen(true);
    };

    const handleCloseEditProfile = () => {
        setEditProfileOpen(false);
    };

    const handleOpenSubscriptionSettings = () => {
        setAnchorEl(null);
        setSubscriptionSettingsOpen(true);
    };

    const handleCloseSubscriptionSettings = () => {
        setSubscriptionSettingsOpen(false);
    };

    const handleSaveProfile = () => {
        // Tutaj można dodać logikę zapisywania danych
        setEditProfileOpen(false);
    };

    const handleProfileChange = (field, value) => {
        setProfileData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleBillingChange = (field, value) => {
        setBillingDetails((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSaveSubscriptionSettings = () => {
        localStorage.setItem('billingDetails', JSON.stringify(billingDetails));
        setSubscriptionSettingsOpen(false);
    };

    const handlePhotoChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfileData(prev => ({
                    ...prev,
                    photo: reader.result
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleBreederLogoChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setBreederForm(prev => ({
                    ...prev,
                    logo: reader.result
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSaveBreeder = () => {
        const dataToSave = {
            name: breederForm.name,
            logo: breederForm.logo,
            address: breederForm.address,
            lat: breederForm.lat || null,
            lng: breederForm.lng || null
        };
        localStorage.setItem('breederData', JSON.stringify(dataToSave));
        setBreederData(dataToSave);
        setEditBreederOpen(false);
    };

    const handleOpenEditBreeder = () => {
        setBreederForm({
            name: breederData.name || '',
            logo: breederData.logo || null,
            address: breederData.address || '',
            lat: breederData.lat || null,
            lng: breederData.lng || null
        });
        if (breederData.lat && breederData.lng) {
            setBreederMapCenter({ lat: breederData.lat, lng: breederData.lng });
            setBreederMapLocation({ lat: breederData.lat, lng: breederData.lng });
        }
        setEditBreederOpen(true);
    };

    const handleBreederGeocode = async () => {
        if (!breederAddressSearch.trim()) return;
        try {
            const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(breederAddressSearch.trim())}&limit=1`, {
                headers: { 'User-Agent': 'PigeonsApp/1.0' }
            });
            const data = await response.json();
            if (data && data.length > 0) {
                const lat = parseFloat(data[0].lat);
                const lng = parseFloat(data[0].lon);
                setBreederMapCenter({ lat, lng });
                setBreederMapLocation({ lat, lng });
                setBreederForm((prev) => ({ ...prev, address: breederAddressSearch.trim(), lat, lng }));
            } else {
                alert('Nie znaleziono lokalizacji. Sprobuj dokladniejszego adresu.');
            }
        } catch (error) {
            console.error('Blad wyszukiwania adresu:', error);
            alert('Nie udalo sie wyszukac adresu.');
        }
    };

    const BreederMapClickHandler = () => {
        useMapEvents({
            click: (e) => {
                setBreederMapLocation({ lat: e.latlng.lat, lng: e.latlng.lng });
                setBreederForm((prev) => ({ ...prev, lat: e.latlng.lat, lng: e.latlng.lng }));
            }
        });
        return null;
    };

    const handleClick = (index, name) => {
        return () => {
            switch(index) {
                case 0: // Panel hodowcy
                    navigate('/panel');
                    break;
                case 1: // Gołębie
                    navigate('/pigeons-new');
                    break;
                case 2: // Rodowody (customIcon4 - genealogy_catalog)
                    navigate('/pedigree');
                    break;
                case 3: // Parowanie (customIcon3 - pair_catalog)
                    navigate('/pairing');
                    break;
                case 4: // Loty
                    navigate('/flights');
                    break;
                default:
                    break;
            }
          };
    }

    const iconLabels = [
        'Panel hodowcy',
        'Gołębie',
        'Rodowody',
        'Parowanie',
        'Loty'
      ];

    const icons = [customIcon1, customIcon2, customIcon4, customIcon3, customIcon5];

    const achievements = React.useMemo(() => {
        const saved = localStorage.getItem('achievementsData');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    return parsed;
                }
            } catch (error) {
                console.error('Blad wczytywania osiagniec:', error);
            }
        }

        const generated = [];
        if (flightsStats.top1Count > 0) {
            generated.push({
                title: 'Mistrzostwo okręgu — kat. A',
                subtitle: `Loty z 1. miejscem: ${flightsStats.top1Count}`,
                badge: '1. miejsce',
                type: 'gold'
            });
        }
        if (flightsStats.top2Count > 0) {
            generated.push({
                title: 'Wicemistrzostwo regionu — kat. B',
                subtitle: `Loty z 2. miejscem: ${flightsStats.top2Count}`,
                badge: '2. miejsce',
                type: 'silver'
            });
        }
        if (flightsStats.top50Count > 0) {
            generated.push({
                title: 'Top 50 — klasyfikacja generalna',
                subtitle: `Loty w top 50: ${flightsStats.top50Count}`,
                badge: 'Top 50',
                type: 'top'
            });
        }
        return generated;
    }, [flightsStats.top1Count, flightsStats.top2Count, flightsStats.top50Count]);

    const recentFlights = React.useMemo(() => {
        const savedFlights = localStorage.getItem('flightsData');
        if (!savedFlights) return [];
        try {
            const flights = JSON.parse(savedFlights);
            if (!Array.isArray(flights)) return [];
            const normalized = flights
                .filter((flight) => flight && flight.name)
                .slice(-5)
                .reverse();
            return normalized;
        } catch (error) {
            console.error('Blad wczytywania ostatnich lotow:', error);
            return [];
        }
    }, [flightsStats.totalFlights]);

    return(
        <div className={styles['content-container']}>
        <Grid container spacing={0} sx={{minHeight: '100vh', margin: 0}}>
          <Grid sx={{minHeight: '100vh', height: '100%', backgroundColor: '#478E94', position: { xs: 'relative', md: 'sticky' }, top: 0, bottom: 0, alignSelf: 'flex-start', margin: 0}} item xs={12} md={2}>
            <Card sx={{
              borderRadius:0,
              backgroundColor:'#478E94',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center', 
              margin: { xs: 1, md: 2 },
              boxShadow: 'none',
            }}>
              <CardMedia
                component="img"
                image={Logo}
                alt="Logo"
                sx={{
                  width: { xs: 120, md: '50%' },
                  maxWidth: '80%',
                  height: 'auto', 
                  background: '#478E94',
                }}
              />
            </Card>
                    
           
                <div className={styles['all-icons-container']}>
                    {icons.map((icon, index) => (
                        <div className={styles['icon-container']} key={index} tabindex="0" onClick={handleClick(index, iconLabels[index])}>
                            <img className={styles['custom-icon']} src={icon} alt={`Custom Icon ${index + 1}`} />
                            <p className={styles['icon-label']}>{iconLabels[index]}</p>
                        </div>
                    ))}
                </div>

                </Grid>
            
                <Grid item xs={12} md={10} sx={{height: { xs: 'auto', md: '100vh' }, overflowY: { xs: 'visible', md: 'auto' }}}>
                    <Box sx={{ background: '#f5f5f5', p: '24px', minHeight: '100%' }}>
                        <Menu
                            id="profile-menu"
                            anchorEl={anchorEl}
                            keepMounted
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                        >
                            <MenuItem onClick={handleMenuClose}>Profil</MenuItem>
                            <MenuItem onClick={handleOpenSubscriptionSettings}>Ustawienia</MenuItem>
                            <MenuItem onClick={handleLogout}>Wyloguj</MenuItem>
                        </Menu>

                        <Box
                            sx={{
                                background: '#2a9d8f',
                                borderRadius: '16px',
                                p: '28px',
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: '88px 1fr auto' },
                                gap: { xs: 2, md: 3 },
                                alignItems: 'center'
                            }}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifySelf: { xs: 'flex-start', md: 'center' } }}>
                                <Avatar
                                    src={profileData.photo || AvatarImage}
                                    sx={{
                                        width: 88,
                                        height: 88,
                                        border: '3px solid rgba(255,255,255,0.4)',
                                        color: '#005f6b',
                                        fontWeight: 700,
                                        fontSize: 24,
                                        background: '#d8e9e6'
                                    }}
                                >
                                    {`${profileData.firstName?.[0] || ''}${profileData.lastName?.[0] || ''}`}
                                </Avatar>
                                <Box
                                    sx={{
                                        mt: 0.8,
                                        px: 1.4,
                                        py: 0.3,
                                        borderRadius: '20px',
                                        background: '#f4a261',
                                        color: '#412402',
                                        fontSize: '10px',
                                        fontWeight: 700
                                    }}
                                >
                                    Plan {planLabel}
                                </Box>
                            </Box>

                            <Box>
                                <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#ffffff', lineHeight: 1.2 }}>
                                    {profileData.firstName} {profileData.lastName}
                                </Typography>
                                <Typography sx={{ mt: 0.4, color: 'rgba(255,255,255,0.75)', fontSize: '16px' }}>
                                    {profileData.city}, Polska
                                </Typography>
                                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                    <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.85)' }}>• {profileData.email}</Typography>
                                    <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.85)' }}>• {profileData.phone}</Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'column' }, gap: 1.2, justifySelf: { xs: 'stretch', md: 'end' }, width: { xs: '100%', md: 'auto' } }}>
                                <Button
                                    onClick={handleOpenEditBreeder}
                                    sx={{
                                        background: 'rgba(255,255,255,0.15)',
                                        border: '0.5px solid rgba(255,255,255,0.4)',
                                        color: '#ffffff',
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        minWidth: 136,
                                        '&:hover': { background: 'rgba(255,255,255,0.22)' }
                                    }}
                                >
                                    Edytuj hodowlę
                                </Button>
                                <Button
                                    onClick={handleEditProfile}
                                    sx={{
                                        background: 'rgba(255,255,255,0.15)',
                                        border: '0.5px solid rgba(255,255,255,0.4)',
                                        color: '#ffffff',
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        minWidth: 136,
                                        '&:hover': { background: 'rgba(255,255,255,0.22)' }
                                    }}
                                >
                                    Edytuj profil
                                </Button>
                                <IconButton
                                    color="inherit"
                                    onClick={handleMenuOpen}
                                    sx={{ alignSelf: { xs: 'center', md: 'flex-end' }, color: '#ffffff' }}
                                >
                                    <ExpandMoreIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '16px' }}>
                            <Box sx={{ background: '#fff', border: '0.5px solid #e5e5e5', borderRadius: '12px', p: '20px' }}>
                                <Typography sx={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#999', mb: 2 }}>
                                    Hodowla
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <Box sx={{ width: 64, height: 64, background: '#e1f5ee', borderRadius: '12px', display: 'grid', placeItems: 'center' }}>
                                        <Bird size={28} color="#2a9d8f" />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: '18px', fontWeight: 500, color: '#151515' }}>
                                            {breederData.name || 'Brak nazwy hodowli'}
                                        </Typography>
                                        <Typography sx={{ fontSize: '13px', color: '#666' }}>
                                            {breederData.address || 'Brak adresu'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <Box sx={{ background: '#fff', border: '0.5px solid #e5e5e5', borderRadius: '12px', p: '20px' }}>
                                <Typography sx={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#999', mb: 2 }}>
                                    Statystyki
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                                    {[
                                        { value: data.pigeonsAmount, label: 'Gołębi' },
                                        { value: data.flightAt, label: 'Wykonanych lotów' },
                                        { value: data.top, label: 'Top 50' },
                                        { value: data.winner, label: 'Zwycięskich lotów' },
                                    ].map((item) => (
                                        <Box key={item.label} sx={{ background: '#f5f5f5', borderRadius: '8px', p: '14px', textAlign: 'center' }}>
                                            <Typography sx={{ fontSize: '24px', color: '#2a9d8f', fontWeight: 600 }}>{item.value}</Typography>
                                            <Typography sx={{ fontSize: '11px', color: '#999' }}>{item.label}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>

                            {achievements.length > 0 && (
                                <Box sx={{ gridColumn: '1 / -1', background: '#fff', border: '0.5px solid #e5e5e5', borderRadius: '12px', p: '20px' }}>
                                    <Typography sx={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#999', mb: 2 }}>
                                        Osiągnięcia
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                                        {achievements.map((achievement, index) => {
                                            const badgeStyles = achievement.type === 'gold'
                                                ? { bg: '#faeeda', color: '#633806' }
                                                : achievement.type === 'silver'
                                                    ? { bg: '#f1efe8', color: '#444' }
                                                    : { bg: '#e1f5ee', color: '#085041' };

                                            const iconNode = achievement.type === 'gold'
                                                ? <Trophy size={17} color="#ba7517" />
                                                : achievement.type === 'silver'
                                                    ? <Medal size={17} color="#888" />
                                                    : <Star size={17} color="#2a9d8f" />;

                                            return (
                                                <Box key={`${achievement.title}-${index}`} sx={{ background: '#f5f5f5', borderRadius: '8px', p: '10px 12px', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Box sx={{ width: 36, height: 36, borderRadius: '50%', background: '#e1f5ee', display: 'grid', placeItems: 'center' }}>
                                                        {iconNode}
                                                    </Box>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{achievement.title}</Typography>
                                                        <Typography sx={{ fontSize: '11px', color: '#999' }}>{achievement.subtitle}</Typography>
                                                    </Box>
                                                    <Box sx={{ px: 1.2, py: 0.5, borderRadius: '999px', background: badgeStyles.bg, color: badgeStyles.color, fontSize: '11px', fontWeight: 600 }}>
                                                        {achievement.badge}
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            )}

                            <Box sx={{ gridColumn: '1 / -1', background: '#fff', border: '0.5px solid #e5e5e5', borderRadius: '12px', p: '20px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <Wind size={16} color="#2a9d8f" />
                                    <Typography sx={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#999' }}>
                                        Ostatnie loty
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                                    {recentFlights.slice(0, 5).map((flight, index) => {
                                        const position = Number(flight.position) || Number(flight.rankingPosition) || null;
                                        const isTop3 = position && position <= 3;
                                        return (
                                            <Box key={`${flight.name}-${index}`} sx={{ background: '#f5f5f5', borderRadius: '8px', p: '10px 12px', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: isTop3 ? '#2a9d8f' : '#9b9b9b' }} />
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{flight.name}</Typography>
                                                    <Typography sx={{ fontSize: '11px', color: '#999' }}>
                                                        {flight.releaseDate || '-'} · {flight.distance ? `${flight.distance} km` : '-'}
                                                    </Typography>
                                                </Box>
                                                <Typography sx={{ fontSize: '13px', fontWeight: 500, color: isTop3 ? '#2a9d8f' : '#666' }}>
                                                    {position ? `${position}. miejsce` : '-'}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                    {recentFlights.length === 0 && (
                                        <Typography sx={{ fontSize: '12px', color: '#999' }}>
                                            Brak danych o lotach.
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Grid>
        </Grid>

        {/* Dialog do edycji profilu */}
        <Dialog open={editProfileOpen} onClose={handleCloseEditProfile} maxWidth="md" fullWidth>
            <DialogTitle sx={{fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '25.2px', color: '#478E95'}}>
                Edytuj profil
            </DialogTitle>
            <DialogContent>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 3, marginTop: 2}}>
                    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2}}>
                        <Avatar 
                            src={profileData.photo} 
                            sx={{width: 135, height: 135}}
                        />
                        <Button 
                            variant="outlined" 
                            component="label"
                            sx={{
                                fontFamily: "'Poppins', sans-serif",
                                textTransform: 'none',
                                borderColor: '#57C09A',
                                color: '#57C09A',
                                '&:hover': {
                                    borderColor: '#4fcda2',
                                    backgroundColor: 'rgba(87, 192, 154, 0.1)'
                                }
                            }}
                        >
                            Zmień zdjęcie
                            <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={handlePhotoChange}
                            />
                        </Button>
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Imię"
                                value={profileData.firstName}
                                onChange={(e) => handleProfileChange('firstName', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        fontFamily: "'Poppins', sans-serif",
                                    },
                                    '& .MuiInputLabel-root': {
                                        fontFamily: "'Poppins', sans-serif",
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Nazwisko"
                                value={profileData.lastName}
                                onChange={(e) => handleProfileChange('lastName', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        fontFamily: "'Poppins', sans-serif",
                                    },
                                    '& .MuiInputLabel-root': {
                                        fontFamily: "'Poppins', sans-serif",
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Miasto"
                                value={profileData.city}
                                onChange={(e) => handleProfileChange('city', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        fontFamily: "'Poppins', sans-serif",
                                    },
                                    '& .MuiInputLabel-root': {
                                        fontFamily: "'Poppins', sans-serif",
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Email"
                                type="email"
                                value={profileData.email}
                                onChange={(e) => handleProfileChange('email', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        fontFamily: "'Poppins', sans-serif",
                                    },
                                    '& .MuiInputLabel-root': {
                                        fontFamily: "'Poppins', sans-serif",
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Telefon"
                                value={profileData.phone}
                                onChange={(e) => handleProfileChange('phone', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        fontFamily: "'Poppins', sans-serif",
                                    },
                                    '& .MuiInputLabel-root': {
                                        fontFamily: "'Poppins', sans-serif",
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions sx={{padding: '20px'}}>
                <Button 
                    onClick={handleCloseEditProfile}
                    sx={{
                        fontFamily: "'Poppins', sans-serif",
                        textTransform: 'none',
                        color: '#707070'
                    }}
                >
                    Anuluj
                </Button>
                <Button 
                    onClick={handleSaveProfile}
                    variant="contained"
                    sx={{
                        fontFamily: "'Poppins', sans-serif",
                        textTransform: 'none',
                        backgroundColor: '#57C09A',
                        '&:hover': {
                            backgroundColor: '#4fcda2',
                        }
                    }}
                >
                    Zapisz
                </Button>
            </DialogActions>
        </Dialog>

        {/* Dialog edycji hodowli */}
        <Dialog open={editBreederOpen} onClose={() => setEditBreederOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle sx={{fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '25.2px', color: '#478E95'}}>
                Edytuj hodowlę
            </DialogTitle>
            <DialogContent>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 3, marginTop: 2}}>
                    <TextField
                        fullWidth
                        label="Nazwa hodowli"
                        value={breederForm.name}
                        onChange={(e) => setBreederForm({...breederForm, name: e.target.value})}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                fontFamily: "'Poppins', sans-serif",
                            },
                            '& .MuiInputLabel-root': {
                                fontFamily: "'Poppins', sans-serif",
                            }
                        }}
                    />
                    
                    <TextField
                        fullWidth
                        label="Adres"
                        multiline
                        rows={3}
                        value={breederForm.address}
                        onChange={(e) => setBreederForm({...breederForm, address: e.target.value})}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                fontFamily: "'Poppins', sans-serif",
                            },
                            '& .MuiInputLabel-root': {
                                fontFamily: "'Poppins', sans-serif",
                            }
                        }}
                    />
                    <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                        <Button
                            variant="outlined"
                            onClick={() => setBreederMapOpen(true)}
                            sx={{
                                fontFamily: "'Poppins', sans-serif",
                                textTransform: 'none',
                                borderColor: '#57C09A',
                                color: '#57C09A'
                            }}
                        >
                            Znajdz adres na mapie
                        </Button>
                        {breederForm.lat && breederForm.lng && (
                            <Typography sx={{fontFamily: "'Poppins', sans-serif", fontSize: '13px', color: '#707070', alignSelf: 'center'}}>
                                Wspolrzedne: {Number(breederForm.lat).toFixed(5)}, {Number(breederForm.lng).toFixed(5)}
                            </Typography>
                        )}
                    </Box>
                    
                    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2}}>
                        {breederForm.logo && (
                            <Box 
                                component="img"
                                src={breederForm.logo}
                                alt="Logo hodowli"
                                sx={{
                                    maxWidth: '300px',
                                    maxHeight: '150px',
                                    objectFit: 'contain',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 2,
                                    padding: 1
                                }}
                            />
                        )}
                        <Button 
                            variant="outlined" 
                            component="label"
                            startIcon={<PhotoCameraIcon />}
                            sx={{
                                fontFamily: "'Poppins', sans-serif",
                                textTransform: 'none',
                                borderColor: '#57C09A',
                                color: '#57C09A',
                                '&:hover': {
                                    borderColor: '#4fcda2',
                                    backgroundColor: 'rgba(87, 192, 154, 0.1)'
                                }
                            }}
                        >
                            {breederForm.logo ? 'Zmień logo/zdjęcie' : 'Dodaj logo/zdjęcie'}
                            <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={handleBreederLogoChange}
                            />
                        </Button>
                        {breederForm.logo && (
                            <Button 
                                variant="outlined" 
                                color="error"
                                onClick={() => setBreederForm({...breederForm, logo: null})}
                                sx={{
                                    fontFamily: "'Poppins', sans-serif",
                                    textTransform: 'none'
                                }}
                            >
                                Usuń logo
                            </Button>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{padding: '20px'}}>
                <Button 
                    onClick={() => setEditBreederOpen(false)}
                    sx={{
                        fontFamily: "'Poppins', sans-serif",
                        textTransform: 'none',
                        color: '#707070'
                    }}
                >
                    Anuluj
                </Button>
                <Button 
                    onClick={handleSaveBreeder}
                    variant="contained"
                    sx={{
                        fontFamily: "'Poppins', sans-serif",
                        textTransform: 'none',
                        backgroundColor: '#57C09A',
                        '&:hover': {
                            backgroundColor: '#4fcda2'
                        }
                    }}
                >
                    Zapisz
                </Button>
            </DialogActions>
        </Dialog>

        <Dialog open={breederMapOpen} onClose={() => setBreederMapOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle sx={{fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '25.2px', color: '#478E95'}}>
                Wyszukaj adres hodowli
            </DialogTitle>
            <DialogContent>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, marginTop: 1}}>
                    <TextField
                        fullWidth
                        label="Pelny adres"
                        placeholder="np. Lodz, Piotrkowska 120"
                        value={breederAddressSearch}
                        onChange={(e) => setBreederAddressSearch(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleBreederGeocode();
                            }
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { fontFamily: "'Poppins', sans-serif" } }}
                    />
                    <Button
                        variant="outlined"
                        onClick={handleBreederGeocode}
                        sx={{width: 'fit-content', fontFamily: "'Poppins', sans-serif", textTransform: 'none'}}
                    >
                        Znajdz na mapie
                    </Button>
                    <Box sx={{ height: '380px', border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
                        <MapContainer
                            center={[breederMapCenter.lat, breederMapCenter.lng]}
                            zoom={10}
                            style={{ height: '100%', width: '100%' }}
                        >
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <BreederMapClickHandler />
                            {breederMapLocation && (
                                <Marker position={[breederMapLocation.lat, breederMapLocation.lng]}>
                                    <Popup>Lokalizacja hodowli</Popup>
                                </Marker>
                            )}
                        </MapContainer>
                    </Box>
                    <Typography sx={{fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#707070'}}>
                        Wpisz dowolny tekst adresu albo kliknij punkt na mapie.
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{padding: '20px'}}>
                <Button onClick={() => setBreederMapOpen(false)} sx={{fontFamily: "'Poppins', sans-serif", textTransform: 'none', color: '#707070'}}>
                    Anuluj
                </Button>
                <Button
                    variant="contained"
                    onClick={() => {
                        if (breederMapLocation) {
                            setBreederForm((prev) => ({ ...prev, lat: breederMapLocation.lat, lng: breederMapLocation.lng }));
                        }
                        setBreederMapOpen(false);
                    }}
                    sx={{fontFamily: "'Poppins', sans-serif", textTransform: 'none', backgroundColor: '#57C09A', '&:hover': { backgroundColor: '#4fcda2' }}}
                >
                    Uzyj tej lokalizacji
                </Button>
            </DialogActions>
        </Dialog>

        <Dialog open={subscriptionSettingsOpen} onClose={handleCloseSubscriptionSettings} maxWidth="sm" fullWidth>
            <DialogTitle sx={{fontWeight: 700, fontSize: '25.2px', color: '#478E95'}}>
                Ustawienia subskrypcji i platnosci
            </DialogTitle>
            <DialogContent>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2.2, marginTop: 1}}>
                    <FormControl fullWidth>
                        <InputLabel>Wybrany plan</InputLabel>
                        <Select
                            value={planId}
                            label="Wybrany plan"
                            onChange={(e) => updatePlan(e.target.value)}
                        >
                            <MenuItem value="free">Free</MenuItem>
                            <MenuItem value="basic">Basic</MenuItem>
                            <MenuItem value="standard">Standard</MenuItem>
                            <MenuItem value="premium">Premium</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        label="Nazwa firmy / hodowli"
                        value={billingDetails.companyName}
                        onChange={(e) => handleBillingChange('companyName', e.target.value)}
                    />
                    <TextField
                        fullWidth
                        label="NIP (opcjonalnie)"
                        value={billingDetails.taxId}
                        onChange={(e) => handleBillingChange('taxId', e.target.value)}
                    />
                    <TextField
                        fullWidth
                        label="Email do faktur"
                        value={billingDetails.billingEmail}
                        onChange={(e) => handleBillingChange('billingEmail', e.target.value)}
                    />
                    <TextField
                        fullWidth
                        label="Metoda platnosci (testowo)"
                        value={billingDetails.paymentMethodNote}
                        onChange={(e) => handleBillingChange('paymentMethodNote', e.target.value)}
                    />
                    <Typography sx={{fontSize: '12px', color: '#707070'}}>
                        Sekcja testowa. Docelowo dane platnosci i status subskrypcji beda synchronizowane przez Stripe + backend.
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{padding: '20px'}}>
                <Button onClick={handleCloseSubscriptionSettings} sx={{textTransform: 'none', color: '#707070'}}>
                    Anuluj
                </Button>
                <Button
                    onClick={handleSaveSubscriptionSettings}
                    variant="contained"
                    sx={{
                        textTransform: 'none',
                        backgroundColor: '#57C09A',
                        '&:hover': { backgroundColor: '#4fcda2' }
                    }}
                >
                    Zapisz ustawienia
                </Button>
            </DialogActions>
        </Dialog>
        </div>
    )
}

export default Panel